import { RotateCcw } from "lucide-react";
import { Fragment, type Ref } from "react";
import type { WhatsAppMessage } from "../api/client";
import { mexicoDateKey } from "../data/format";
import { deliveryState } from "../data/whatsapp-delivery";
import { WhatsAppMessageBody } from "./WhatsAppMessageBody";

const time = (value: string) =>
  new Intl.DateTimeFormat("es-MX", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  }).format(new Date(value));

function dayLabel(value: string, now = new Date()) {
  const key = mexicoDateKey(new Date(value));
  const today = mexicoDateKey(now);
  const yesterday = mexicoDateKey(new Date(now.getTime() - 86400000));
  if (key === today) return "Hoy";
  if (key === yesterday) return "Ayer";
  const label = new Intl.DateTimeFormat("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "America/Mexico_City",
  }).format(new Date(value));
  return label.charAt(0).toLocaleUpperCase("es-MX") + label.slice(1);
}

/** Outbound only: in a one-to-one chat the left side is always the customer. */
const sender = (message: WhatsAppMessage) =>
  message.direction === "inbound"
    ? null
    : message.sender_type === "assistant"
      ? "Asistente"
      : message.sender_type === "system"
        ? "Sistema"
        : "Tú";

/**
 * WhatsApp-style thread shared by Mensajes and the case page: bubbles sized to their text,
 * time inside the bubble and one separator per day instead of a full date on every message.
 */
export function WhatsAppMessages({
  messages,
  listRef,
  canRetry,
  retryDisabled,
  onRetry,
}: {
  messages: WhatsAppMessage[];
  listRef?: Ref<HTMLDivElement>;
  canRetry: boolean;
  retryDisabled: boolean;
  onRetry: (message: WhatsAppMessage) => void;
}) {
  return (
    <div ref={listRef} className="whatsapp-messages" aria-live="polite">
      {messages.map((message, index) => {
        const previous = messages[index - 1];
        const newDay =
          !previous ||
          mexicoDateKey(new Date(previous.created_at)) !==
            mexicoDateKey(new Date(message.created_at));
        const delivery =
          message.direction === "outbound"
            ? deliveryState(message.status, message.error_code)
            : null;
        const retryable = Boolean(
          message.outbox_id &&
            ["failed", "delivery_uncertain", "quarantined"].includes(message.status),
        );
        const who = sender(message);
        return (
          <Fragment key={message.id}>
            {newDay && (
              <p className="whatsapp-day" role="separator">
                <span>{dayLabel(message.created_at)}</span>
              </p>
            )}
            <article
              className={`whatsapp-message whatsapp-message--${message.direction} whatsapp-message--${message.status}${message.sender_type === "assistant" ? " whatsapp-message--assistant" : ""}`}
            >
              <WhatsAppMessageBody
                body={message.body}
                messageType={message.message_type}
                metadata={message.metadata}
                messageId={message.id}
                media={message.media}
              />
              <div className="whatsapp-message__meta">
                {who && <span className="whatsapp-message__sender">{who}</span>}
                {delivery && delivery.tone !== "sent" && (
                  <span
                    className={`whatsapp-message__delivery whatsapp-message__delivery--${delivery.tone}`}
                    title={message.error_code ?? undefined}
                  >
                    {delivery.label}
                  </span>
                )}
                {retryable && canRetry && (
                  <button
                    type="button"
                    className="whatsapp-message__retry"
                    onClick={() => onRetry(message)}
                    disabled={retryDisabled}
                  >
                    <RotateCcw />
                    Reintentar
                  </button>
                )}
                <time dateTime={message.created_at} title={dayLabel(message.created_at)}>
                  {time(message.created_at)}
                </time>
                {delivery?.tone === "sent" && (
                  <span className="whatsapp-message__check" aria-label="Enviado" title="Enviado">
                    ✓
                  </span>
                )}
              </div>
            </article>
          </Fragment>
        );
      })}
    </div>
  );
}
